import cn from 'classnames';
import type { FC, ReactNode } from 'react';
import React from 'react';

import { Text } from '@sravni/react-design-system/lib/Typography';

import type { FILTERS_NAMES } from '@src/constants/filters';
import { useBrandingContent } from '@src/reducers/branding';
import { CalculatorSponsorBanner } from 'entities/Banner/ui/CalculatorSponsorBanner/CalculatorSponsorBanner';
import { CALCULATOR_SPONSOR_PROMO_DOCUMENT_LINK } from 'entities/Banner/ui/CalculatorSponsorBanner/CalculatorSponsorBanner.config';
import { CalculatorSponsorDisclaimer } from 'entities/Banner/ui/CalculatorSponsorBanner/components/CalculatorSponsorDisclaimer';
import { FullDealEntryPointBanner } from 'entities/FullDeal';
import { TO_FULL_DEAL_BUTTON_DEFAULT_TEXT } from 'entities/FullDeal/config';
import { FullDealEntryPointDisclaimer } from 'entities/FullDeal/ui/FullDealEntryPointDisclaimer/FullDealEntryPointDisclaimer';
import { InlineCalculations } from 'entities/FullDeal/ui/InlineCalculations/InlineCalculations';
import { FullDealEntryPointForm, FullDealMobileAppActions, FullDealOfferActions } from 'features/FullDeal';
import { formatToFullDealButtonText, openPersonalProfile } from 'features/FullDeal/lib';

import { useCalculator } from '../../../../lib';
import type { RenderActionsProps } from '../../../../types';

import styles from './FullDealEntryPointDefault.module.scss';

interface Props {
  isHiddenBorder?: boolean;
  heading?: string;

  isLanding?: boolean;
  highlightOffer?: boolean;

  onChange?: (key: FILTERS_NAMES, value: number) => void;
  renderActions?: (props: RenderActionsProps) => ReactNode;
}

export const FullDealEntryPointDefault: FC<Props> = ({
  heading,
  highlightOffer = true,
  onChange,
  renderActions,
  isLanding,
  isHiddenBorder,
}) => {
  const branding = useBrandingContent();

  const {
    hasOfferOrForm,
    isMobile,
    toFullDealButtonText,
    amount,
    term,
    handleChange,
    isShowCalculator,
    hasOffer,
    hasIssuedMicrocreditDeals,
  } = useCalculator(onChange);

  const _toFullDealButtonText = formatToFullDealButtonText(
    toFullDealButtonText || branding?.sponsor?.buttonText || TO_FULL_DEAL_BUTTON_DEFAULT_TEXT,
  );

  const highlightActiveOffer = hasOfferOrForm && highlightOffer;
  const ActionsComponent = isMobile ? FullDealMobileAppActions : FullDealOfferActions;

  const container = (
    <div
      className={cn(styles.container, {
        [styles.withOfferOrForm]: highlightActiveOffer,
        [styles.hiddenBorder]: isHiddenBorder && !highlightActiveOffer,
      })}
    >
      <div className={styles.content}>
        <FullDealEntryPointBanner
          mobileBanner={<CalculatorSponsorBanner isShowDisplayPixel={false} bannerVariant="entryPoint" />}
          isLanding={isLanding}
          heading="Подберем займ"
          hasOffer={hasOffer}
        />
        <div className={styles.formSection}>
          {isShowCalculator && (
            <FullDealEntryPointForm amount={amount} term={term} onChange={handleChange} isCustomTheme />
          )}

          <InlineCalculations isMobile={isMobile} amount={amount} term={term as number}>
            {!renderActions && (
              <ActionsComponent
                style={branding.sponsor.buttonStyles}
                amount={amount}
                term={term}
                className={styles.accentButton}
                toFullDealButtonColor="green"
                hasIssuedMicrocreditDeals={hasIssuedMicrocreditDeals}
                toFullDealButtonText={_toFullDealButtonText}
                onMyLoansClick={openPersonalProfile}
                seoHeading={heading}
              />
            )}

            {renderActions?.({
              amount,
              term,
              hasOffer,
              toFullDealButtonText: _toFullDealButtonText,
            })}
          </InlineCalculations>

          <div className={styles.mobileFooter}>
            <FullDealEntryPointDisclaimer link={CALCULATOR_SPONSOR_PROMO_DOCUMENT_LINK} />
            <CalculatorSponsorDisclaimer heading={branding?.sponsor?.title} className={styles.mobileLegalDisclaimer} />
          </div>
        </div>
      </div>

      <div className={cn('mobile:hidden', styles.serviceOneContainer)}>
        <CalculatorSponsorBanner isShowDisplayPixel />
      </div>
    </div>
  );

  return (
    <div
      className={cn(styles.relativeContainer, {
        [styles.asLandingContainer]: isLanding,
        [styles.relativeHighlighter]: highlightActiveOffer,
      })}
    >
      {highlightActiveOffer ? (
        <div className={cn(styles.highlightCard, { [styles.hiddenBorder]: isHiddenBorder })}>
          <Text strong className={styles.withOfferOrFormText}>
            Продолжите оформление займа
          </Text>
          {container}
        </div>
      ) : (
        container
      )}
    </div>
  );
};
